import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Shield, LogOut, Calendar, Phone, Trash2, Globe } from 'lucide-react';
import { authAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const ProfilePage: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>((user as any)?.languagePreference || i18n.language || 'en');

  // Sync selectedLanguage with user's languagePreference when user changes
  useEffect(() => {
    if (user && (user as any)?.languagePreference) {
      setSelectedLanguage((user as any).languagePreference);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRequestDeletion = async () => {
    if (!window.confirm('Are you sure you want to initiate account deletion? Your data will be permanently deleted after the grace period. You can cancel this within the grace period.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await authAPI.requestAccountDeletion(deletionReason.trim() || undefined);
      if (response.success) {
        await updateUser(); // Refresh user data
        setShowDeletionModal(false);
        setDeletionReason('');
        const gracePeriodDays = response.data?.gracePeriodDays || 7;
        const deletionDate = response.data?.deletionDate ? new Date(response.data.deletionDate).toLocaleDateString() : '';
        alert(`Account deletion initiated. Your data will be permanently deleted in ${gracePeriodDays} days (${deletionDate}). You can cancel this deletion by logging in within the next ${gracePeriodDays} days.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit deletion request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!window.confirm('Are you sure you want to cancel the account deletion? Your account will remain active.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await authAPI.cancelAccountDeletion();
      if (response.success) {
        await updateUser(); // Refresh user data
        alert('Account deletion has been cancelled successfully. Your account is now active.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel account deletion');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">You need to be logged in to view this page.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>

      {/* Profile Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Profile Header */}
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="h-8 w-8 text-black" />
              </div>
              <div className="ml-6">
                <h2 className="text-2xl font-bold text-black">{user.name}</h2>
                <p className="text-gray-600 capitalize">{user.role} Account</p>
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>
              
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Email Address</p>
                  <p className="text-black">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center">
                <Shield className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Account Type</p>
                  <p className="text-black capitalize">{user.role}</p>
                </div>
              </div>

              {user.phone && (
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${
                        user.phoneVerified 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {user.phoneVerified ? (
                          <>
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            OTP Verified
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Unverified
                          </>
                        )}
                      </span>
                    </div>
                    <p className="text-black">{user.phone}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Member Since</p>
                  <p className="text-black">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Recently joined'}
                  </p>
                </div>
              </div>

              {user.role === 'provider' && (user as any)?.bio && (
                <div className="flex items-start">
                  <User className="h-5 w-5 text-gray-400 mr-3 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Bio</p>
                    <p className="text-black">{(user as any).bio}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <Globe className="h-5 w-5 text-gray-400 mr-3" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-2">{t('settings.languagePreference')}</p>
                  <select
                    value={selectedLanguage}
                    onChange={async (e) => {
                      const newLang = e.target.value;
                      setSelectedLanguage(newLang);
                      try {
                        // Change language immediately
                        await i18n.changeLanguage(newLang);
                        // Update backend
                        await authAPI.updateProfileData({ languagePreference: newLang });
                        await updateUser();
                      } catch (err) {
                        console.error('Failed to update language preference:', err);
                        // Revert language change on error
                        const currentLang = (user as any)?.languagePreference || 'en';
                        setSelectedLanguage(currentLang);
                        await i18n.changeLanguage(currentLang);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-black focus:ring-2 focus:ring-black focus:border-transparent"
                  >
                    <option value="en">English</option>
                    <option value="hi">हिंदी (Hindi)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Account Actions */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => navigate('/edit-profile')}
                className="flex-1 bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-colors"
              >
                Edit Profile
              </button>
              {/* Change Password Button - Hidden */}
              <button 
                onClick={handleLogout}
                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors"
              >
                <LogOut className="h-4 w-4 inline mr-2" />
                Sign Out
              </button>
            </div>
            
            {/* Account Deletion Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-black mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-600 mb-4">
                Request to delete your account. This action requires admin approval.
              </p>
              {(user as any)?.status === 'deleted' ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">
                        Account Deletion Initiated
                      </p>
                      {(user as any)?.deletedAt && (() => {
                        const deletedAt = new Date((user as any).deletedAt);
                        const gracePeriodDays = (user as any)?.gracePeriodDays || 7;
                        const deletionDate = new Date(deletedAt);
                        deletionDate.setDate(deletionDate.getDate() + gracePeriodDays);
                        const now = new Date();
                        const daysRemaining = Math.ceil((deletionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <>
                            <p className="text-xs text-yellow-700 mt-1">
                              Your data will be permanently deleted in {gracePeriodDays} days ({deletionDate.toLocaleDateString()}).
                            </p>
                            {daysRemaining > 0 && (
                              <p className="text-xs text-yellow-600 mt-1">
                                {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining. You can cancel this deletion.
                              </p>
                            )}
                            <button
                              onClick={handleCancelDeletion}
                              disabled={loading}
                              className="mt-3 flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              <Shield className="h-4 w-4 mr-2" />
                              Cancel Deletion
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowDeletionModal(true)}
                  className="flex items-center px-4 py-2 bg-red-600/60 text-white rounded-md hover:bg-red-600/70 transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Request Account Deletion
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">💾 Data Storage Notice</h3>
          <p className="text-blue-800 text-sm">
            <strong>Current Setup:</strong> This application uses in-memory storage for development. 
            Your data will be reset when the server restarts. In production, all data would be 
            permanently stored in a MongoDB database.
          </p>
        </div>
      </div>

      {/* Account Deletion Modal */}
      {showDeletionModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeletionModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-white">
                      Request Account Deletion
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Are you sure you want to request account deletion? This action requires admin approval. 
                        Once approved, your account and all associated data will be permanently deleted.
                      </p>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reason (Optional)
                        </label>
                        <textarea
                          value={deletionReason}
                          onChange={(e) => setDeletionReason(e.target.value)}
                          placeholder="Please provide a reason for account deletion (optional)"
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {deletionReason.length}/500 characters
                        </p>
                      </div>

                      {error && (
                        <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      )}

                      <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-500">
                        <p className="text-sm text-yellow-700">
                          ⚠️ This action cannot be undone once approved by an admin.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleRequestDeletion}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeletionModal(false);
                    setDeletionReason('');
                    setError('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage; 